/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  emptyOf,
  getSlicedData,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { SubOfficeDetailsMapper } from './mappers/sub-office-details.mapper';
import { SubOfficeListItemMapper } from './mappers/sub-office-list-item.mapper';
import { OfficeListItemMapper } from '../offices/mappers/office-list-item.mapper';
import { OfficeListItemDto } from '../offices/models/office-list-item.dto';
import { SubOfficeDetailsDto } from './models/sub-office-details.dto';
import { SubOfficeListItemDto } from './models/sub-office-list-item.dto';
import { OfficeDetailsDto, OfficeDetailsMapper } from '../offices';

@Injectable()
export class SubOfficeRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  get(id: ID): Observable<SubOfficeDetailsDto> {
    return this.httpGet(`${this.bp}/sub-offices/${id}`, optimisticLockingOf(SubOfficeDetailsMapper));
  }

  create(details: SubOfficeDetailsDto): Observable<SubOfficeDetailsDto> {
    return this.httpPost(`${this.bp}/sub-offices`, details, optimisticLockingOf(SubOfficeDetailsMapper));
  }

  update(details: SubOfficeDetailsDto): Observable<SubOfficeDetailsDto> {
    return this.httpPut(`${this.bp}/sub-offices/${details.id}`, details, optimisticLockingOf(SubOfficeDetailsMapper));
  }

  deactivate(office: SubOfficeListItemDto): Observable<SubOfficeListItemDto> {
    return this.httpPut(
      `${this.bp}/sub-offices/${office.subOfficeId}/deactivate`,
      office,
      emptyOf(optimisticLockingOf(SubOfficeListItemMapper)),
    );
  }

  delete(office: SubOfficeListItemDto): Observable<SubOfficeListItemDto> {
    return this.httpDelete(
      `${this.bp}/sub-offices/${office.subOfficeId}`,
      office,
      emptyOf(optimisticLockingOf(SubOfficeListItemMapper)),
    );
  }

  findAllAsAutocompleteDataSource(name: string): SlicedDataSource<SubOfficeListItemDto> {
    return this.createSlicedDataSource({
      method: RequestMethod.Get,
      body: { name },
      url: `${this.bp}/sub-offices?autocomplete=true`,
      responseMapper: arrayOf(SubOfficeListItemMapper),
    });
  }

  findAllAsDataSource(): SlicedDataSource<SubOfficeListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/sub-offices`, arrayOf(SubOfficeListItemMapper));
  }

  getFranchiseeOffices(): Observable<OfficeListItemDto[]>  {
    return this.createSlicedDataSourceGet(`${this.bp}/offices`, arrayOf(OfficeListItemMapper)).select({ from: 0, to: 1000 }).pipe(
      map$(getSlicedData));
  }

  getFranchiseeOffice(id: ID): Observable<OfficeDetailsDto> {
    return this.httpGet(`${this.bp}/offices/${id}`, optimisticLockingOf(OfficeDetailsMapper));
  }

}
